import {
  ColorTokens,
  interaction,
  SizableText,
  XStack,
  YStack,
} from '@mythical/ui'
import { SmartLink } from 'app/components/primitive/SmartLink'
import { ColorEnum, mapColor } from 'app/utils/mapColors'
import { SolitoImage } from 'solito/image'

export interface ProductCardProps {
  id: string
  image: string
  title?: string
  discount?: number // 0 - 100
  price: number
  description?: string
  href: string
  backgroundColour?: ColorEnum
  forceDarkTheme?: boolean
}

export const ProductCard = (props: ProductCardProps) => {
  const {
    image,
    title,
    discount,
    price,
    description,
    href,
    backgroundColour,
    forceDarkTheme,
  } = props

  const discountedPrice = discount ? price - (price * discount) / 100 : null
  const bgColor: ColorTokens = forceDarkTheme
    ? '$bgTertiary'
    : mapColor(backgroundColour)

  return (
    <SmartLink
      Component={YStack}
      href={href}
      animation="medium"
      gap="$2"
      width="100%"
      $gtSm={{ gap: '$2.5' }}
      {...interaction.opacity}
    >
      <YStack
        ai="center"
        bg={bgColor}
        br="$2"
        jc="center"
        overflow="hidden"
        position="relative"
        width="100%"
      >
        {!!discount && (
          <XStack
            bg="$bgPrimary"
            br="$pill"
            left="$1.5"
            position="absolute"
            px="$2"
            py="$1.5"
            top="$1.5"
            zIndex={1}
          >
            <SizableText size="$1" color="$fgPrimary" fow="600" tt="uppercase">
              {discount}% OFF
            </SizableText>
          </XStack>
        )}

        <YStack width="100%" aspectRatio={1} position="relative">
          <SolitoImage
            alt={title || ''}
            contentFit="contain"
            fill
            sizes="(min-width: 660px) 277px, 170px"
            src={image}
          />
        </YStack>
      </YStack>

      <XStack gap="$2.5" ai="flex-start" width="100%">
        <YStack gap="$1.5" $gtSm={{gap: '$2'}} flex={1}>
          {title && (
            <SizableText
              size="$1"
              color="$fgPrimary"
              fow="500"
              $gtSm={{
                size: '$2',
                fontWeight: '500',
              }}
            >
              {title}
            </SizableText>
          )}

          {!!description && (
            <SizableText
              size="$0.75"
              color="$fgSecondary"
              fontWeight="500"
              $gtSm={{
                size: '$1',
                fontWeight: '500',
              }}
            >
              {description}
            </SizableText>
          )}
        </YStack>

        <XStack gap="$2" ai="center">
          <SizableText
            size="$1"
            color="$fgPrimary"
            fow="500"
            $gtSm={{
              size: '$4',
              fontWeight: '500',
            }}
          >
            ${Math.round(discountedPrice ?? price)}
          </SizableText>

          {!!discountedPrice && (
            <SizableText
              size="$1"
              color="$fgSecondary"
              fow="500"
              textDecorationLine="line-through"
              $gtSm={{
                size: '$4',
                fontWeight: '500',
              }}
            >
              ${Math.round(price)}
            </SizableText>
          )}
        </XStack>
      </XStack>
    </SmartLink>
  )
}
